// src/AuthContext.tsx
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api, { setUnauthorizedHandler } from '../axios/axiosConfig';
import {
  getUser,
  registerPushNotification,
  unRegisterPushNotification,
  getUserVehicles,
} from '../services/userService';
import { useBrandContext } from './BrandContext';
import { DEFAULT_BRAND_NAME } from '../constants/brand';
import { authenticateWithGoogle } from '../services/googleAuthService';
import { ReactComponent as LoaderSecondary } from '../assets/SpinnerBlack.svg';
import { useLocationContext } from './LocationContext';
import { buildUserTags } from '../utils/buildUserTags';

export type AuthContextType = {
  token: string | null | undefined;
  expoPushToken: string | null;
  storedDevicePushToken: string | null;
  storedDevicePushTokenType: string | null;
  storedNotificationsPermission: string | null;
  storedGpsPermission: string | null;
  storedCameraPermission: string | null;
  platform: string | null;
  appVersion: string | null;
  user: any | null;
  isLogged: () => void | boolean | undefined;
  setToken: (token: string | null, key?: string) => void;
  setUser: (user: any | null) => void;
  reloadUserData: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
};

function checkNotificationPermission(): boolean {
  console.log('Check Notifications permission');

  // navigator.permissions.query({ name: "push" })
  //     .then((result) => {
  //         if (result.state === "denied") {
  //             console.error('Notifications permission has been denied');
  //             return false;
  //         }
  //     })
  //     .catch((error) =>{
  //         console.error('Error checking Notifications permission:', error);
  //         return false;
  //     });

  return true;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const expoPushToken: string | null = localStorage.getItem('expoPushToken');
  const storedDevicePushToken: string | null = localStorage.getItem('devicePushToken');
  const storedDevicePushTokenType: string | null = localStorage.getItem('devicePushTokenType');
  const storedNotificationsPermission: string | null = localStorage.getItem('notificationsPermission');
  const storedGpsPermission: string | null = localStorage.getItem('gpsPermission');
  const storedCameraPermission: string | null = localStorage.getItem('cameraPermission');
  const platform: string | null = localStorage.getItem('platform');
  const appVersion: string | null = localStorage.getItem('appVersion');

  console.log('AuthProvider - localStorage data:', {
    expoPushToken,
    storedDevicePushToken,
    storedDevicePushTokenType,
    storedNotificationsPermission,
    storedGpsPermission,
    storedCameraPermission,
    platform,
    appVersion,
  });
  const [token, setTokenInternal] = useState<string | null | undefined>();
  const [user, setUser] = useState<any | null>();
  const brandContext = useBrandContext();
  const locationContext = useLocationContext();
  const [tokenStorageKey, setTokenStorageKey] = useState<string>('');
  const [isSocialLogin, setIsSocialLogin] = useState(false);
  const [tokenRegistered, setTokenRegistered] = useState(false);
  const [pendingTokenRegistration, setPendingTokenRegistration] = useState(false);
  const [lastGpsState, setLastGpsState] = useState<boolean | null>(null);
  const lastUserRef = useRef<any>(null);
  const isLoggingOutRef = useRef(false);

  const isFetchingLoginRef = useRef(false);

  const isLogged = (): boolean | undefined => {
    return token === undefined ? undefined : token ? true : false;
  };

  const reloadUserData = async () => {
    if (!token) {
      console.log('reloadUserData: No token available');
      return;
    }

    try {
      console.log('reloadUserData: Fetching updated user and vehicles data');
      const [userData, vehicles] = await Promise.all([
        getUser(),
        getUserVehicles(),
      ]);

      const userWithData = {
        ...userData,
        is_logged: true,
        userTags: buildUserTags(userData, true, vehicles || []),
        vehicles: vehicles || [],
      };

      lastUserRef.current = userWithData;
      setUser(userWithData);

      console.log('reloadUserData: User data reloaded successfully', {
        username: userData.username,
        vehicleCount: vehicles?.length || 0,
        tags: userWithData.userTags,
      });
    } catch (error) {
      console.error('reloadUserData: Error reloading user data', error);
    }
  };

  const setToken = (token: string | null) => {
    if (platform && token !== undefined) {
      api.defaults.headers.common['User-Agent'] = platform;
    }

    if (token === null) {
      localStorage.removeItem(tokenStorageKey);
      delete api.defaults.headers.common.Authorization;
      
      // Enviar logout com tags is_logged: false e dados do último usuário
      if (lastUserRef.current) {
        const logoutUser = {
          ...lastUserRef.current,
          is_logged: false,
          userTags: buildUserTags(lastUserRef.current, false, lastUserRef.current.vehicles || []),
        };
        setUser(logoutUser);
        console.log('User logged out with tags is_logged: false', logoutUser.userTags);
        
        // Após enviar o evento para o WebView, limpar o usuário
        setTimeout(() => {
          setUser(null);
          lastUserRef.current = null;
        }, 100);
      } else {
        setUser(null);
      }
    } else if (token) {
      console.log('setToken tokenStorageKey: ' + tokenStorageKey);
      localStorage.setItem(tokenStorageKey, token);
      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Carregar dados do usuário e lista de veículos antes de enviar para o WebView
      Promise.all([
        getUser(),
        getUserVehicles(),
      ]).then(([user, vehicles]) => {
        // Adicionar tags de usuário e lista de veículos
        const userWithData = {
          ...user,
          is_logged: true,
          userTags: buildUserTags(user, true, vehicles || []),
          vehicles: vehicles || [],
        };
        lastUserRef.current = userWithData;
        setUser(userWithData);
        console.log('User data loaded with vehicles:', {
          username: user.username,
          vehicleCount: vehicles?.length || 0,
        });

        // Registrar token de notificação existente após login
        const hasToken = storedDevicePushToken || expoPushToken;
        if (hasToken) {
          // Verificar se GPS está habilitado (storedGpsPermission === 'granted')
          const gpsEnabled = storedGpsPermission === 'granted';
          
          console.log('Login token registration strategy', {
            hasDeviceToken: !!storedDevicePushToken,
            hasExpoToken: !!expoPushToken,
            gpsEnabled: gpsEnabled,
            hasUserLocation: !!locationContext.userLocation,
            appId: brandContext.appId,
          });

          // Se GPS está habilitado, aguardar localização antes de registrar
          if (gpsEnabled && !locationContext.userLocation) {
            console.log('GPS enabled but no location yet - waiting for location to register token');
            setPendingTokenRegistration(true);
          } else {
            // GPS desabilitado ou já temos localização - registrar imediatamente
            console.log('Registering token immediately after login', {
              reason: gpsEnabled ? 'GPS enabled with location' : 'GPS not enabled',
              deviceTokenType: storedDevicePushTokenType || 'expo (default)',
            });

            if (storedDevicePushToken) {
              // Usar o tipo específico do device token (firebase/apn) ou fallback para expo
              const notificationProvider = storedDevicePushTokenType || 'expo';
              registerPushNotification(storedDevicePushToken, platform, appVersion, notificationProvider, brandContext.appId);
            } else if (expoPushToken) {
              registerPushNotification(expoPushToken, platform, appVersion, 'expo', brandContext.appId);
            }
            setTokenRegistered(true);
          }
        }
      });

      checkNotificationPermission();
    }

    // Login novo reabilita o logout automatico.
    if (token) isLoggingOutRef.current = false;

    setTokenInternal(token);
  };

  const contextValue: AuthContextType = {
    token,
    setToken,
    expoPushToken,
    storedDevicePushToken,
    storedDevicePushTokenType,
    storedNotificationsPermission,
    storedGpsPermission,
    storedCameraPermission,
    platform,
    appVersion,
    user,
    setUser,
    isLogged,
    reloadUserData,
  };

  useEffect(() => {
    if (!tokenStorageKey) return;
    console.log('getToken tokenStorageKey: ' + tokenStorageKey);
    setToken(localStorage.getItem(tokenStorageKey));
  }, [tokenStorageKey]);

  // Logout automatico quando a API indica que o usuario nao esta mais
  // autorizado, foi bloqueado ou excluido. Os endpoints e status que disparam
  // isso estao definidos em axios/axiosConfig (shouldForceLogout).
  // Replica o processo do botao de sair (handleSignOut).
  // Mesma regra do spott-cms (performLogout).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Evita que varias requisicoes falhando ao mesmo tempo disparem o fluxo
      // de logout mais de uma vez.
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;

      console.log('Logout automatico: /userprofile rejeitou a sessao');

      const pushToken = storedDevicePushToken || expoPushToken;
      const notificationProvider = storedDevicePushToken
        ? storedDevicePushTokenType || 'expo'
        : 'expo';

      unRegisterPushNotification(
        pushToken,
        platform,
        appVersion,
        notificationProvider,
        brandContext.appId
      );

      setToken(null);
      navigate(`/${brandContext.brandName || DEFAULT_BRAND_NAME}/login`, {
        replace: true,
      });
    });

    return () => setUnauthorizedHandler(null);
  }, [tokenStorageKey, brandContext.brandName, brandContext.appId]);

  useEffect(() => {
    if (!brandContext.brandData?.appBrand) return;
    setTokenStorageKey(`token-${brandContext.brandData?.appBrand}`);
  }, [brandContext.brandData?.appBrand]);

  // Registrar token quando localização estiver disponível ou GPS desabilitado
  useEffect(() => {
    console.log('AuthContext - Location update', {
      isGpsLocation: locationContext.isGpsLocation,
      userLocation: locationContext.userLocation,
      pendingTokenRegistration: pendingTokenRegistration,
      tokenRegistered: tokenRegistered,
      userLocationHeader: api.defaults.headers.common['User-Location'],
    });

    if (!isLogged()) return;

    const hasToken = storedDevicePushToken || expoPushToken;
    if (!hasToken) return;

    // CASO 1: Token pendente de registro (login com GPS habilitado) - aguardando localização
    if (pendingTokenRegistration) {
      if (locationContext.userLocation) {
        console.log('Location received - registering pending token', {
          hasLocation: !!locationContext.userLocation,
          deviceTokenType: storedDevicePushTokenType || 'expo (default)',
          appId: brandContext.appId,
        });

        if (storedDevicePushToken) {
          const notificationProvider = storedDevicePushTokenType || 'expo';
          registerPushNotification(storedDevicePushToken, platform, appVersion, notificationProvider, brandContext.appId);
        } else if (expoPushToken) {
          registerPushNotification(expoPushToken, platform, appVersion, 'expo', brandContext.appId);
        }
        setPendingTokenRegistration(false);
        setTokenRegistered(true);
      }
      return;
    }

    // CASO 2: GPS já estava habilitado (usuário já logado) - re-registrar quando GPS muda
    if (lastGpsState !== null && lastGpsState !== locationContext.isGpsLocation) {
      console.log('GPS state changed - re-registering token', {
        previousGpsState: lastGpsState,
        currentGpsState: locationContext.isGpsLocation,
        deviceTokenType: storedDevicePushTokenType || 'expo (default)',
        appId: brandContext.appId,
      });

      if (storedDevicePushToken) {
        const notificationProvider = storedDevicePushTokenType || 'expo';
        registerPushNotification(storedDevicePushToken, platform, appVersion, notificationProvider, brandContext.appId);
      } else if (expoPushToken) {
        registerPushNotification(expoPushToken, platform, appVersion, 'expo', brandContext.appId);
      }
    }

    setLastGpsState(locationContext.isGpsLocation);
  }, [
    locationContext.userLocation,
    locationContext.isGpsLocation,
    token,
    storedDevicePushToken,
    expoPushToken,
    platform,
    appVersion,
    brandContext.appId,
    pendingTokenRegistration,
    lastGpsState,
  ]);

  useEffect(() => {
    console.log('location', location);
    if (isFetchingLoginRef.current || !tokenStorageKey) return;
    const asyncHandler = async () => {
      console.log(window.location.hash);
      const hash = window.location.hash.substring(1);
      const search = window.location.search.substring(1);
      console.log('hash', hash);
      const paramsHash = new URLSearchParams(hash);
      const paramsSearch = new URLSearchParams(search);
      console.log('params', paramsHash);
      const access_token = paramsHash.get('access_token');
      console.log('access_token', access_token);
      const token = paramsSearch.get('token');
      if (access_token || token) {
        isFetchingLoginRef.current = true;
        setIsSocialLogin(true);
        let success = false;
        if (access_token) {
          console.log('ENTROU access_token');
          success = await authenticateWithGoogle(access_token, contextValue);
        }
        if (token) {
          console.log('ENTROU token', token);
          success = true;
          contextValue.setToken(token);
        }
        if (success) {
          // Volta para a rota que o ProtectedRoute barrou (ex.: carregador aberto pelo QR code),
          // como o login por senha já faz.
          const attemptRoute = localStorage.getItem('attemptRoute');
          localStorage.removeItem('attemptRoute');
          setTimeout(() => {
            navigate(`/${brandContext.brandName}${attemptRoute || '/'}`);
            setIsSocialLogin(false);
          }, 1000);
        } else {
          setIsSocialLogin(false);
        }
      }
    };
    asyncHandler();
  }, [location, tokenStorageKey]);

  return isSocialLogin ? (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoaderSecondary className='spinner' />
    </div>
  ) : (
    <AuthContext.Provider value={contextValue}>
      {isLogged() !== undefined && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
